import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, ShieldOff, Copy, Download, RefreshCw, Smartphone } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, Button, Field, Input, PasswordInput } from "./ui-kit";

const errorText = (e) => formatApiErrorDetail(e.response?.data?.detail);

function RecoveryCodes({ codes, onDone }) {
  const [saved, setSaved] = useState(false);
  const text = `DF HACCP — codici di recupero (ognuno vale una volta sola)\n\n${codes.join("\n")}\n`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Codici copiati"); }
    catch { toast.error("Copia non riuscita: scrivili a mano"); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url; a.download = "df-haccp-codici-recupero.txt"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4" data-testid="two-factor-recovery-codes">
      <p className="text-sm text-slate-600 font-body">
        Salva questi codici in un posto sicuro (gestore di password o stampati). Servono solo se perdi il telefono:
        ognuno vale una volta sola. <strong>Non verranno più mostrati.</strong>
      </p>
      <ul className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-4 font-mono text-sm text-navy">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={copy} data-testid="two-factor-copy-codes"><Copy className="w-4 h-4" /> Copia</Button>
        <Button variant="outline" onClick={download} data-testid="two-factor-download-codes"><Download className="w-4 h-4" /> Scarica</Button>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600 font-body">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="h-4 w-4 accent-navy" data-testid="two-factor-codes-saved" />
        Ho salvato i codici
      </label>
      <Button variant="success" className="w-full" disabled={!saved} onClick={onDone} data-testid="two-factor-codes-done">Fatto</Button>
    </div>
  );
}

export default function TwoFactorSettings() {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("idle"); // idle | password | scan | codes | renew | disable
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus((await api.get("/auth/2fa/status")).data); }
    catch (e) { toast.error(errorText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setStep("idle"); setPassword(""); setCode(""); setSetup(null); };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  const startSetup = () => run(async () => {
    setSetup((await api.post("/auth/2fa/setup", { password })).data);
    setPassword(""); setCode(""); setStep("scan");
  });
  const enable = () => run(async () => {
    const { data } = await api.post("/auth/2fa/enable", { code });
    setCodes(data.recovery_codes); setCode(""); setStep("codes");
    toast.success("Verifica in due passaggi attivata");
    await load();
  });
  const renewCodes = () => run(async () => {
    const { data } = await api.post("/auth/2fa/recovery-codes", { password, code });
    setCodes(data.recovery_codes); setPassword(""); setCode(""); setStep("codes");
  });
  const disable = () => {
    if (!window.confirm("Disattivare la verifica in due passaggi? Per entrare basterà di nuovo la sola password.")) return;
    run(async () => {
      await api.post("/auth/2fa/disable", { password, code });
      toast.success("Verifica in due passaggi disattivata");
      reset(); await load();
    });
  };
  const forgetDevices = () => {
    if (!window.confirm("Dimenticare tutti i dispositivi ricordati? Al prossimo accesso il codice verrà chiesto ovunque, anche qui.")) return;
    run(async () => {
      await api.post("/auth/2fa/forget-devices");
      toast.success("Dispositivi dimenticati");
    });
  };

  if (!status) return null;

  const passwordAndCode = (onConfirm, label, testid) => (
    <div className="space-y-4">
      <Field label="Password attuale *"><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} data-testid={`${testid}-password`} /></Field>
      <Field label="Codice dell'app o di recupero *">
        <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" className="font-mono tracking-[0.2em]" data-testid={`${testid}-code`} />
      </Field>
      <div className="flex gap-2">
        <Button variant="outline" onClick={reset}>Annulla</Button>
        <Button variant={testid === "two-factor-disable" ? "danger" : "primary"} className="flex-1" loading={busy}
          disabled={!password || !code} onClick={onConfirm} data-testid={`${testid}-submit`}>{label}</Button>
      </div>
    </div>
  );

  return (
    <Card className="p-6 space-y-4" data-testid="two-factor-settings">
      <div className="flex items-start gap-3">
        {status.enabled ? <ShieldCheck className="w-6 h-6 text-haccp-success shrink-0" /> : <ShieldOff className="w-6 h-6 text-slate-400 shrink-0" />}
        <div>
          <h2 className="font-heading text-lg font-bold">Verifica in due passaggi</h2>
          <p className="text-sm text-slate-500 font-body" data-testid="two-factor-state">
            {status.enabled
              ? `Attiva. Dopo la password serve il codice dell'app sul telefono; i dispositivi ricordati lo chiedono ogni ${status.remember_days} giorni. Codici di recupero rimasti: ${status.recovery_codes_left}.`
              : "Non attiva. Con la verifica, a chi conosce la tua password serve anche il tuo telefono per entrare."}
          </p>
        </div>
      </div>

      {step === "codes" && <RecoveryCodes codes={codes} onDone={() => { setCodes([]); reset(); load(); }} />}

      {!status.enabled && step === "idle" && (
        <Button variant="primary" className="w-full" onClick={() => setStep("password")} data-testid="two-factor-start">
          <Smartphone className="w-4 h-4" /> Attiva la verifica in due passaggi
        </Button>
      )}

      {step === "password" && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 font-body">
            Ti serve un'app di autenticazione sul telefono, per esempio Google Authenticator o Microsoft Authenticator (gratuite).
          </p>
          <Field label="Password attuale *"><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} data-testid="two-factor-setup-password" /></Field>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset}>Annulla</Button>
            <Button variant="primary" className="flex-1" loading={busy} disabled={!password} onClick={startSetup} data-testid="two-factor-setup-submit">Continua</Button>
          </div>
        </div>
      )}

      {step === "scan" && setup && (
        <div className="space-y-4">
          <ol className="list-decimal pl-5 text-sm text-slate-600 font-body space-y-1">
            <li>Nell'app di autenticazione tocca «+» e inquadra il codice QR.</li>
            <li>Scrivi qui sotto le 6 cifre che compaiono per «DF HACCP».</li>
          </ol>
          <div className="flex justify-center rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <QRCodeSVG value={setup.otpauth_uri} size={200} data-testid="two-factor-qr" />
          </div>
          <p className="text-xs text-slate-500 font-body break-all">
            Non riesci a inquadrarlo? Inserisci a mano questa chiave: <span className="font-mono text-navy" data-testid="two-factor-secret">{setup.secret}</span>
          </p>
          <Field label="Codice di 6 cifre *">
            <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="font-mono tracking-[0.3em]" data-testid="two-factor-enable-code" />
          </Field>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset}>Annulla</Button>
            <Button variant="success" className="flex-1" loading={busy} disabled={code.length !== 6} onClick={enable} data-testid="two-factor-enable-submit">Attiva</Button>
          </div>
        </div>
      )}

      {status.enabled && step === "idle" && (
        <div className="grid gap-2 sm:grid-cols-2">
          <Button variant="outline" onClick={() => setStep("renew")} data-testid="two-factor-renew"><RefreshCw className="w-4 h-4" /> Nuovi codici di recupero</Button>
          <Button variant="outline" loading={busy} onClick={forgetDevices} data-testid="two-factor-forget-devices">Dimentica i dispositivi ricordati</Button>
          <Button variant="danger" className="sm:col-span-2" onClick={() => setStep("disable")} data-testid="two-factor-disable">Disattiva</Button>
        </div>
      )}
      {step === "renew" && passwordAndCode(renewCodes, "Genera nuovi codici", "two-factor-renew")}
      {step === "disable" && passwordAndCode(disable, "Disattiva", "two-factor-disable")}
    </Card>
  );
}
